import { motion, AnimatePresence } from "framer-motion";
import {
  FileText,
  X,
  Building2,
  Shield,
  User,
  AlertCircle,
  MapPin,
  FileCheck,
  CheckCircle,
  XCircle,
  Clock,
} from "lucide-react";
import { format, parseISO } from "date-fns";

const StatusBadge = ({ status }) => {
  const configs = {
    pending: {
      icon: Clock,
      color: "bg-yellow-100 text-yellow-800 border-yellow-200",
      label: "Pending",
    },
    received: {
      icon: FileCheck,
      color: "bg-blue-100 text-blue-800 border-blue-200",
      label: "Received",
    },
    reviewing: {
      icon: AlertCircle,
      color: "bg-indigo-100 text-indigo-800 border-indigo-200",
      label: "Reviewing",
    },
    reviewed: {
      icon: AlertCircle,
      color: "bg-indigo-100 text-indigo-800 border-indigo-200",
      label: "Reviewed",
    },
    claimed: {
      icon: CheckCircle,
      color: "bg-green-100 text-green-800 border-green-200",
      label: "Claimed",
    },
    rejected: {
      icon: XCircle,
      color: "bg-red-100 text-red-800 border-red-200",
      label: "Rejected",
    },
  };

  const config = configs[status] || configs.pending;
  const Icon = config.icon;

  return (
    <motion.span
      initial={{ scale: 0.9, opacity: 0 }}
      animate={{ scale: 1, opacity: 1 }}
      className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium border ${config.color}`}
    >
      <Icon className="mr-1 w-3 h-3" />
      {config.label}
    </motion.span>
  );
};

const ClaimDetailsModal = ({ isOpen, onClose, claim }) => {
  if (!claim) return null;

  const incident = claim.incident?.[0] || {};

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4"
            onClick={onClose}
          >
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="absolute inset-0 bg-black bg-opacity-50 backdrop-blur-sm"
            />
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              onClick={(e) => e.stopPropagation()}
              className="relative w-full max-w-4xl max-h-[90vh] overflow-y-auto bg-white rounded-2xl shadow-2xl"
            >
              {/* Header */}
              <div className="sticky top-0 z-10 flex items-center justify-between p-6 bg-gradient-to-r from-blue-600 to-indigo-600 rounded-t-2xl">
                <div className="flex items-center space-x-3">
                  <div className="p-2 bg-white/20 rounded-lg">
                    <FileText className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h2 className="text-xl font-bold text-white">Claim Details</h2>
                    <p className="text-sm text-blue-100">Claim ID: {claim.slug?.slice(0, 8).toUpperCase()}...</p>
                  </div>
                </div>
                <button
                  onClick={onClose}
                  className="p-2 text-white hover:bg-white/20 rounded-lg transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              {/* Content */}
              <div className="p-6 space-y-6">
                {/* Status & Basic Info */}
                <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
                  <div className="p-4 bg-gray-50 rounded-xl">
                    <p className="text-sm font-medium text-gray-600 mb-2">Status</p>
                    <StatusBadge status={claim.claim_status} />
                  </div>
                  <div className="p-4 bg-gray-50 rounded-xl">
                    <p className="text-sm font-medium text-gray-600 mb-2">Policy Slug</p>
                    <p className="text-sm font-mono font-semibold text-gray-900">{claim.policy_slug || "N/A"}</p>
                  </div>
                  <div className="p-4 bg-gray-50 rounded-xl">
                    <p className="text-sm font-medium text-gray-600 mb-2">Provider</p>
                    <p className="text-sm font-semibold text-gray-900">
                      {claim.insurance_provider?.company_name || claim.insurance_provider_slug || "N/A"}
                    </p>
                  </div>
                </div>

                {/* Rejection Reason - Show prominently for rejected claims */}
                {(claim.claim_status === "rejected" && (claim.reject_reason || claim.policy?.reject_reason)) && (
                  <div className="p-5 bg-gradient-to-br from-red-50 to-rose-50 rounded-xl border-2 border-red-200">
                    <h3 className="flex items-center mb-3 text-lg font-bold text-red-900">
                      <XCircle className="mr-2 w-5 h-5 text-red-600" />
                      Rejection Reason
                    </h3>
                    <div className="p-4 bg-white rounded-lg border border-red-200">
                      <p className="text-sm text-red-800 leading-relaxed">
                        {claim.reject_reason || claim.policy?.reject_reason || "No reason provided"}
                      </p>
                    </div>
                  </div>
                )}

                {/* Provider Information */}
                {claim.insurance_provider && (
                  <div className="p-5 bg-gradient-to-br from-purple-50 to-pink-50 rounded-xl border border-purple-100">
                    <h3 className="flex items-center mb-4 text-lg font-bold text-gray-900">
                      <Building2 className="mr-2 w-5 h-5 text-purple-600" />
                      Provider Information
                    </h3>
                    <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                      <div className="md:col-span-2 flex items-center">
                        {claim.insurance_provider.company_logo && (
                          <img 
                            src={claim.insurance_provider.company_logo} 
                            alt={claim.insurance_provider.company_name}
                            className="mr-3 w-12 h-12 rounded object-cover"
                          />
                        )}
                        <div>
                          <p className="text-sm font-semibold text-gray-900">{claim.insurance_provider.company_name || "N/A"}</p>
                          {claim.insurance_provider.location && (
                            <p className="text-xs text-gray-600">{claim.insurance_provider.location}, {claim.insurance_provider.region}</p>
                          )}
                        </div>
                      </div>
                      <div>
                        <p className="text-xs font-medium text-gray-600 mb-1">Email</p>
                        <p className="text-sm font-semibold text-gray-900">{claim.insurance_provider.email || "N/A"}</p>
                      </div>
                      <div>
                        <p className="text-xs font-medium text-gray-600 mb-1">Phone Number</p>
                        <p className="text-sm font-semibold text-gray-900">{claim.insurance_provider.phone_number || "N/A"}</p>
                      </div>
                      <div>
                        <p className="text-xs font-medium text-gray-600 mb-1">Contact Person</p>
                        <p className="text-sm font-semibold text-gray-900">{claim.insurance_provider.contact_person || "N/A"}</p>
                      </div>
                      <div>
                        <p className="text-xs font-medium text-gray-600 mb-1">NIC Number</p>
                        <p className="text-sm font-semibold text-gray-900">{claim.insurance_provider.nic_number || "N/A"}</p>
                      </div>
                      <div>
                        <p className="text-xs font-medium text-gray-600 mb-1">Status</p>
                        <span className={`inline-flex items-center px-2.5 py-1 rounded-md text-xs font-medium ${
                          claim.insurance_provider.status === 1 
                            ? "bg-green-100 text-green-800" 
                            : "bg-gray-100 text-gray-800"
                        }`}>
                          {claim.insurance_provider.status === 1 ? "Active" : "Inactive"}
                        </span>
                      </div>
                    </div>
                  </div>
                )}

                {/* User Information */}
                {claim.user && (
                  <div className="p-5 bg-gradient-to-br from-green-50 to-emerald-50 rounded-xl border border-green-100">
                    <h3 className="flex items-center mb-4 text-lg font-bold text-gray-900">
                      <User className="mr-2 w-5 h-5 text-green-600" />
                      User Information
                    </h3>
                    <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                      <div>
                        <p className="text-xs font-medium text-gray-600 mb-1">Full Name</p>
                        <p className="text-sm font-semibold text-gray-900">
                          {claim.user.first_name} {claim.user.last_name}
                        </p>
                      </div>
                      <div>
                        <p className="text-xs font-medium text-gray-600 mb-1">Email</p>
                        <p className="text-sm font-semibold text-gray-900">{claim.user.email || "N/A"}</p>
                      </div>
                      <div>
                        <p className="text-xs font-medium text-gray-600 mb-1">Phone Number</p>
                        <p className="text-sm font-semibold text-gray-900">{claim.user.phone_number || "N/A"}</p>
                      </div>
                      <div>
                        <p className="text-xs font-medium text-gray-600 mb-1">Location</p>
                        <p className="text-sm font-semibold text-gray-900">{claim.user.location || "N/A"}</p>
                      </div>
                      <div>
                        <p className="text-xs font-medium text-gray-600 mb-1">Region</p>
                        <p className="text-sm font-semibold text-gray-900">{claim.user.region || "N/A"}</p>
                      </div>
                      <div>
                        <p className="text-xs font-medium text-gray-600 mb-1">Gender</p>
                        <p className="text-sm font-semibold text-gray-900 capitalize">{claim.user.gender || "N/A"}</p>
                      </div>
                      <div>
                        <p className="text-xs font-medium text-gray-600 mb-1">User Slug</p>
                        <p className="text-sm font-mono text-gray-700">{claim.user.slug || "N/A"}</p>
                      </div>
                      <div>
                        <p className="text-xs font-medium text-gray-600 mb-1">For Self</p>
                        <p className="text-sm font-semibold text-gray-900">{claim.for_self ? "Yes" : "No"}</p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Incident Details */}
                <div className="p-5 bg-gradient-to-br from-red-50 to-orange-50 rounded-xl border border-red-100">
                  <h3 className="flex items-center mb-4 text-lg font-bold text-gray-900">
                    <AlertCircle className="mr-2 w-5 h-5 text-red-600" />
                    Incident Information
                  </h3>
                  <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                    <div>
                      <p className="text-xs font-medium text-gray-600 mb-1">Date</p>
                      <p className="text-sm font-semibold text-gray-900">
                        {incident.date ? format(parseISO(incident.date), "MMM dd, yyyy") : "N/A"}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs font-medium text-gray-600 mb-1">Type</p>
                      <p className="text-sm font-semibold text-gray-900">{incident.type || "N/A"}</p>
                    </div>
                    <div className="md:col-span-2">
                      <p className="text-xs font-medium text-gray-600 mb-1">Location</p>
                      <p className="flex items-center text-sm font-semibold text-gray-900">
                        <MapPin className="mr-1 w-4 h-4" />
                        {incident.location || "N/A"}
                      </p>
                    </div>
                    <div className="md:col-span-2">
                      <p className="text-xs font-medium text-gray-600 mb-1">Description</p>
                      <p className="text-sm text-gray-700">{incident.description || "N/A"}</p>
                    </div>
                    <div>
                      <p className="text-xs font-medium text-gray-600 mb-1">Authorities Notified</p>
                      <p className="text-sm font-semibold text-gray-900 capitalize">{incident.authorities || "N/A"}</p>
                    </div>
                  </div>
                </div>

                {/* Policy Information */}
                {claim.policy && (
                  <div className="p-5 bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl border border-blue-100">
                    <h3 className="flex items-center mb-4 text-lg font-bold text-gray-900">
                      <Shield className="mr-2 w-5 h-5 text-blue-600" />
                      Policy Information
                    </h3>
                    <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                      <div>
                        <p className="text-xs font-medium text-gray-600 mb-1">Policy Name</p>
                        <p className="text-sm font-semibold text-gray-900">{claim.policy.policy_name || "N/A"}</p>
                      </div>
                      <div>
                        <p className="text-xs font-medium text-gray-600 mb-1">Policy Type</p>
                        <p className="text-sm font-semibold text-gray-900 capitalize">
                          {claim.policy.policy_type?.replace(/_/g, " ") || "N/A"}
                        </p>
                      </div>
                      <div>
                        <p className="text-xs font-medium text-gray-600 mb-1">Geography</p>
                        <p className="text-sm font-semibold text-gray-900 capitalize">
                          {claim.policy.geography || "N/A"}
                        </p>
                      </div>
                      <div>
                        <p className="text-xs font-medium text-gray-600 mb-1">Risk Level</p>
                        <p className="text-sm font-semibold text-gray-900 capitalize">
                          {claim.policy.risk_level || "N/A"}
                        </p>
                      </div>
                      <div>
                        <p className="text-xs font-medium text-gray-600 mb-1">Status</p>
                        <span className={`inline-flex items-center px-2.5 py-1 rounded-md text-xs font-medium ${
                          claim.policy.status === "approved" 
                            ? "bg-green-100 text-green-800" 
                            : "bg-yellow-100 text-yellow-800"
                        }`}>
                          {claim.policy.status || "N/A"}
                        </span>
                      </div>
                      <div>
                        <p className="text-xs font-medium text-gray-600 mb-1">Max Claims</p>
                        <p className="text-sm font-semibold text-gray-900">
                          {claim.policy.max_claims || "N/A"}
                        </p>
                      </div>
                      <div className="md:col-span-2">
                        <p className="text-xs font-medium text-gray-600 mb-1">Description</p>
                        <p className="text-sm text-gray-700">{claim.policy.description || "N/A"}</p>
                      </div>
                    </div>

                    {/* Coverage Durations/Packages - Different structures for different policy types */}
                    {claim.policy.coverage_durations && claim.policy.coverage_durations.length > 0 && (
                      <div className="mt-4 pt-4 border-t border-blue-200">
                        <p className="text-xs font-medium text-gray-600 mb-3">
                          {claim.policy.policy_type === "intercity_travel" ? "Coverage Durations" : "Available Packages"}
                        </p>
                        <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                          {claim.policy.coverage_durations.map((item, idx) => {
                            // Intercity Travel Structure
                            if (claim.policy.policy_type === "intercity_travel") {
                              return (
                                <div key={idx} className="p-3 bg-white rounded-lg border border-gray-200">
                                  <div className="flex items-center justify-between mb-2">
                                    <span className="text-sm font-semibold text-gray-900">
                                      {item.display_value} {item.display_unit}
                                    </span>
                                    <span className="text-sm font-bold text-emerald-600">₵{item.price}</span>
                                  </div>
                                  {item.route_destination && (
                                    <p className="text-xs text-gray-600">Route: {item.route_destination}</p>
                                  )}
                                  {item.route_type && (
                                    <p className="text-xs text-gray-500 capitalize">Type: {item.route_type}</p>
                                  )}
                                </div>
                              );
                            }
                            // Other Policy Types (Travel, Home, Life Insurance) - Package Structure
                            else {
                              return (
                                <div key={idx} className="p-4 bg-gradient-to-br from-blue-50 to-indigo-50 rounded-lg border border-blue-200">
                                  <div className="flex items-center justify-between mb-2">
                                    <h5 className="text-sm font-bold text-gray-900">{item.product_name || `Package ${idx + 1}`}</h5>
                                    <div className="text-right">
                                      <span className="text-sm font-bold text-emerald-600">₵{item.premium}</span>
                                      {item.premium_duration && (
                                        <span className="text-xs text-gray-500 block capitalize">/{item.premium_duration}</span>
                                      )}
                                    </div>
                                  </div>
                                  {item.duration && (
                                    <p className="text-xs text-gray-600 mb-2">
                                      <span className="font-medium">Duration:</span> {item.duration}
                                    </p>
                                  )}
                                  {item.description && (
                                    <p className="text-xs text-gray-700 mb-3">{item.description}</p>
                                  )}
                                  
                                  {/* Package-specific Coverage List */}
                                  {item.insurance_coverage_list && item.insurance_coverage_list.length > 0 && (
                                    <div className="mb-3 pt-3 border-t border-blue-200">
                                      <p className="text-xs font-semibold text-gray-700 mb-2">Coverage:</p>
                                      <div className="space-y-1">
                                        {item.insurance_coverage_list.map((coverage, cIdx) => (
                                          <div key={cIdx} className="text-xs">
                                            <span className="font-medium text-gray-900">{coverage.title}</span>
                                            {coverage.limit && (
                                              <span className="text-emerald-600 ml-2">₵{coverage.limit}</span>
                                            )}
                                          </div>
                                        ))}
                                      </div>
                                    </div>
                                  )}

                                  {/* Package-specific Exclusions */}
                                  {item.exclusions && item.exclusions.length > 0 && (
                                    <div className="mb-2 pt-2 border-t border-blue-200">
                                      <p className="text-xs font-semibold text-red-700 mb-1">Exclusions:</p>
                                      <ul className="space-y-0.5">
                                        {item.exclusions.map((exclusion, eIdx) => (
                                          <li key={eIdx} className="text-xs text-red-600">• {exclusion}</li>
                                        ))}
                                      </ul>
                                    </div>
                                  )}

                                  {/* Package-specific Eligibility */}
                                  {item.eligibility_criteria && item.eligibility_criteria.length > 0 && (
                                    <div className="pt-2 border-t border-blue-200">
                                      <p className="text-xs font-semibold text-gray-700 mb-1">Eligibility:</p>
                                      <ul className="space-y-0.5">
                                        {item.eligibility_criteria.map((criteria, crIdx) => (
                                          <li key={crIdx} className="text-xs text-gray-600">• {criteria}</li>
                                        ))}
                                      </ul>
                                    </div>
                                  )}
                                </div>
                              );
                            }
                          })}
                        </div>
                      </div>
                    )}

                    {/* General Coverage Information (for intercity_travel or when no package-specific coverage) */}
                    {claim.policy.insurance_coverage_list && claim.policy.insurance_coverage_list.length > 0 && 
                     (claim.policy.policy_type === "intercity_travel" || !claim.policy.coverage_durations?.[0]?.insurance_coverage_list) && (
                      <div className="mt-4 pt-4 border-t border-blue-200">
                        <p className="text-xs font-medium text-gray-600 mb-2">Coverage Details</p>
                        <div className="space-y-2">
                          {claim.policy.insurance_coverage_list.map((coverage, idx) => (
                            <div key={idx} className="p-3 bg-white rounded-lg border border-gray-200">
                              <div className="flex items-start justify-between">
                                <div className="flex-1">
                                  <p className="text-sm font-semibold text-gray-900">{coverage.title}</p>
                                  {coverage.description && (
                                    <p className="text-xs text-gray-600 mt-1">{coverage.description}</p>
                                  )}
                                </div>
                                {coverage.limit && (
                                  <span className="text-sm font-bold text-green-600 ml-2">Limit: ₵{coverage.limit}</span>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Eligibility Criteria */}
                    {claim.policy.eligibility_criteria && claim.policy.eligibility_criteria.length > 0 && (
                      <div className="mt-4 pt-4 border-t border-blue-200">
                        <p className="text-xs font-medium text-gray-600 mb-2">Eligibility Criteria</p>
                        <ul className="space-y-1">
                          {claim.policy.eligibility_criteria.map((criteria, idx) => (
                            <li key={idx} className="flex items-start text-sm text-gray-700">
                              <CheckCircle className="mr-2 mt-0.5 w-4 h-4 text-blue-600 flex-shrink-0" />
                              <span>{criteria}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}

                    {/* Exclusions */}
                    {claim.policy.exclusions && claim.policy.exclusions.length > 0 && (
                      <div className="mt-4 pt-4 border-t border-blue-200">
                        <p className="text-xs font-medium text-gray-600 mb-2">Exclusions</p>
                        <ul className="space-y-1">
                          {claim.policy.exclusions.map((exclusion, idx) => (
                            <li key={idx} className="flex items-start text-sm text-red-700">
                              <XCircle className="mr-2 mt-0.5 w-4 h-4 text-red-600 flex-shrink-0" />
                              <span>{exclusion}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}

                    {/* Claim Process Steps */}
                    {claim.policy.claim_process_steps && claim.policy.claim_process_steps.length > 0 && (
                      <div className="mt-4 pt-4 border-t border-blue-200">
                        <p className="text-xs font-medium text-gray-600 mb-2">Claim Process Steps</p>
                        <div className="space-y-3">
                          {claim.policy.claim_process_steps.map((step, idx) => (
                            <div key={idx} className="p-3 bg-white rounded-lg border border-gray-200">
                              <div className="flex items-start">
                                <div className="flex-shrink-0 w-6 h-6 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center text-xs font-bold mr-3">
                                  {step.step_number || idx + 1}
                                </div>
                                <div className="flex-1">
                                  <p className="text-sm font-semibold text-gray-900">{step.title}</p>
                                  {step.description && (
                                    <p className="text-xs text-gray-600 mt-1">{step.description}</p>
                                  )}
                                  <div className="flex items-center gap-4 mt-2 text-xs text-gray-500">
                                    {step.timeframe && (
                                      <span className="flex items-center">
                                        <Clock className="mr-1 w-3 h-3" />
                                        {step.timeframe}
                                      </span>
                                    )}
                                    {step.requirements && (
                                      <span className="flex items-center">
                                        <FileText className="mr-1 w-3 h-3" />
                                        {step.requirements}
                                      </span>
                                    )}
                                  </div>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Terms */}
                    {claim.policy.terms && (
                      <div className="mt-4 pt-4 border-t border-blue-200">
                        <p className="text-xs font-medium text-gray-600 mb-1">Terms & Conditions</p>
                        {claim.policy.terms.startsWith("http") ? (
                          <a 
                            href={claim.policy.terms} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="text-xs text-blue-600 hover:underline"
                          >
                            {claim.policy.terms}
                          </a>
                        ) : (
                          <p className="text-xs text-gray-700">{claim.policy.terms}</p>
                        )}
                      </div>
                    )}
                  </div>
                )}

                {/* Supporting Documents */}
                {claim.supporting_documents && claim.supporting_documents.length > 0 && (
                  <div className="p-5 bg-gray-50 rounded-xl">
                    <h3 className="flex items-center mb-4 text-lg font-bold text-gray-900">
                      <FileCheck className="mr-2 w-5 h-5 text-gray-600" />
                      Supporting Documents
                    </h3>
                    <div className="grid grid-cols-2 gap-3 md:grid-cols-3">
                      {claim.supporting_documents.map((doc, idx) => (
                        <a
                          key={idx}
                          href={doc}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center justify-center p-3 bg-white rounded-lg border border-gray-200 hover:border-blue-300 hover:shadow-md transition-all"
                        >
                          <FileText className="mr-2 w-4 h-4 text-blue-600" />
                          <span className="text-xs font-medium text-gray-700">Doc {idx + 1}</span>
                        </a>
                      ))}
                    </div>
                  </div>
                )}

                {/* Declarations */}
                {claim.declarations && claim.declarations.length > 0 && (
                  <div className="p-5 bg-green-50 rounded-xl border border-green-100">
                    <h3 className="flex items-center mb-4 text-lg font-bold text-gray-900">
                      <CheckCircle className="mr-2 w-5 h-5 text-green-600" />
                      Declarations
                    </h3>
                    <ul className="space-y-2">
                      {claim.declarations.map((declaration, idx) => (
                        <li key={idx} className="flex items-start text-sm text-gray-700">
                          <CheckCircle className="mr-2 mt-0.5 w-4 h-4 text-green-600 flex-shrink-0" />
                          <span>{declaration}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            </motion.div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default ClaimDetailsModal;
